import { useEffect, useMemo, useRef, useState } from "react";

import type { MenuItem } from "@sytofood/shared";

import {
  type DailySummaryView,
  type LoggedMeal,
  type ParseMealView,
  type TargetsView,
  api,
} from "../lib/api";
import { todayIso } from "../lib/day";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";
import { fileToCompressedDataUrl } from "../lib/image";
import { scoreMenuItem } from "../lib/menu-score";
import { ScreenShell } from "./screen-shell";
import { IconCamera } from "./ui/icon";
import { AiFooter, Chip, Conf, H1, MonoMeta, TopBar, confLevelFromScore } from "./ui/primitives";

type Props = {
  householdId: string;
  targets: TargetsView;
  onLogged: (meal: LoggedMeal) => void;
  onUseAsMeal: (parsed: ParseMealView) => void;
  onBack: () => void;
};

type Mode = "top3" | "all";


function remainingOf(targets: TargetsView, totals: DailySummaryView["totals"]) {
  return {
    kcal: targets.kcalTarget !== null ? Math.max(0, targets.kcalTarget - totals.kcal) : null,
    proteinG:
      targets.proteinTargetG !== null
        ? Math.max(0, targets.proteinTargetG - totals.proteinG)
        : null,
  };
}

export function MenuPhotoScreen({ householdId, targets, onLogged, onUseAsMeal, onBack }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const [phase, setPhase] = useState<"pick" | "parsed">("pick");
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [showTranslated, setShowTranslated] = useState(true);
  const [mode, setMode] = useState<Mode>("top3");
  const [summary, setSummary] = useState<DailySummaryView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loggingIdx, setLoggingIdx] = useState<number | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Menu is translated into the app's selected language, not the browser's.
  const targetLanguage = lang;

  useEffect(() => {
    let cancelled = false;
    api
      .getDailySummary(householdId, todayIso())
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch(() => {
        // Not fatal — ranking falls back to confidence-only when no budget known.
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  const remaining = summary ? remainingOf(targets, summary.totals) : { kcal: null, proteinG: null };

  const sameLanguage = detectedLanguage === targetLanguage || detectedLanguage === null;
  const effectiveShowTranslated = showTranslated && !sameLanguage;

  const ranked = useMemo(() => {
    if (mode === "all") return items;
    return [...items]
      .sort((a, b) => scoreMenuItem(b, remaining) - scoreMenuItem(a, remaining))
      .slice(0, 3);
  }, [items, mode, remaining]);

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      // Up to 3 photos (multi-page menus). 2048px long edge — small print
      // needs OCR-grade detail.
      const picked = files.slice(0, 3);
      const dataUrls = await Promise.all(
        picked.map((f) => fileToCompressedDataUrl(f, { maxEdge: 2048 })),
      );
      setPreviews(dataUrls);
      const res = await api.parseMenuPhoto(householdId, {
        imageDataUrls: dataUrls,
        targetLanguage,
      });
      if (res.items.length === 0) {
        setError(t.menuErrNoMenu);
        return;
      }
      setItems(res.items);
      setDetectedLanguage(res.detectedLanguage);
      setPhase("parsed");
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errSave);
    } finally {
      setSubmitting(false);
    }
  }

  async function quickLog(item: MenuItem, idx: number) {
    setLoggingIdx(idx);
    setError(null);
    try {
      const { meals } = await api.saveMeals(
        {
          items: [
            {
              name: effectiveShowTranslated ? item.nameTranslated : item.name,
              kcal: item.estimatedKcal,
              proteinG: item.estimatedProteinG,
              fatG: item.estimatedFatG,
              carbsG: item.estimatedCarbsG,
              portionGrams: null,
              confidence: item.confidence,
            },
          ],
          source: "manual_ai",
        },
        householdId,
      );
      const meal = meals[0];
      if (meal) onLogged(meal);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errLog);
    } finally {
      setLoggingIdx(null);
    }
  }

  function editBeforeLog(item: MenuItem) {
    onUseAsMeal({
      items: [
        {
          name: effectiveShowTranslated ? item.nameTranslated : item.name,
          kcal: item.estimatedKcal,
          proteinG: item.estimatedProteinG,
          fatG: item.estimatedFatG,
          carbsG: item.estimatedCarbsG,
          portionGrams: null,
          confidence: item.confidence,
        },
      ],
      source: "manual_ai",
      rawInputText: `Menu: ${item.name}`,
    });
  }

  function reset() {
    setPhase("pick");
    setPreviews([]);
    setItems([]);
    setDetectedLanguage(null);
    setError(null);
    setExpandedIdx(null);
    setMode("top3");
  }

  return (
    <ScreenShell hideHeader withBottomNav>
      <div style={{ marginBottom: 18 }}>
        <TopBar
          back={t.menuBack}
          title={t.menuTopTitle}
          onBack={onBack}
          right={
            phase === "parsed" ? (
              <button
                type="button"
                onClick={reset}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--accent)",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {t.menuOtherPhoto}
              </button>
            ) : undefined
          }
        />
      </div>

      {phase === "pick" ? (
        <>
          <H1 accent="?">{t.menuPickTitle}</H1>
          <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>{t.menuPickIntro}</p>

          <div
            style={{
              marginTop: 24,
              padding: 16,
              borderRadius: 14,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {previews.length > 0 ? (
              <div style={{ display: "flex", gap: 8 }}>
                {previews.map((src, i) => (
                  <img
                    key={src.slice(0, 48) + i}
                    src={src}
                    alt={t.menuPreviewAlt(i + 1)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      aspectRatio: "3 / 4",
                      objectFit: "cover",
                      borderRadius: 10,
                      background: "var(--surface-2)",
                    }}
                  />
                ))}
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  borderRadius: 10,
                  background: "var(--surface-2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--muted)",
                  fontSize: 13,
                }}
              >
                {t.menuPickPlaceholder}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                if (files.length > 0) handleFiles(files);
                e.target.value = "";
              }}
              className="hidden"
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 uppercase disabled:opacity-50"
              style={{
                padding: "16px 20px",
                borderRadius: 12,
                background: "var(--accent)",
                color: "var(--accent-ink)",
                border: "none",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                boxShadow: "var(--shadow-cta)",
              }}
            >
              <IconCamera size={18} />
              {submitting ? t.menuReading : t.menuPickCta}
            </button>

            {error ? (
              <p
                className="text-[12px] px-3 py-2 rounded-md"
                style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
              >
                {error}
              </p>
            ) : null}

            <AiFooter />
          </div>
        </>
      ) : (
        <>
          <H1 accent=".">{t.menuResultTitle}</H1>
          <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
            <span
              className="tabular"
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                color: "var(--ink)",
              }}
            >
              {t.menuDishCount(items.length)}
            </span>
            {remaining.kcal !== null ? (
              <>
                {t.menuRemainingPrefix}
                <span style={{ color: "var(--accent)", fontWeight: 700 }}>
                  {t.menuRemaining(remaining.kcal)}
                </span>
              </>
            ) : null}
          </p>

          {/* filter chips */}
          <div style={{ marginTop: 18, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Chip active={mode === "top3"} onClick={() => setMode("top3")}>
              {t.menuFilterTop3}
            </Chip>
            <Chip active={mode === "all"} onClick={() => setMode("all")}>
              {t.menuFilterAll}
            </Chip>
            {!sameLanguage ? (
              <Chip active={!showTranslated} onClick={() => setShowTranslated((v) => !v)}>
                {showTranslated
                  ? t.menuShowOriginal(detectedLanguage ?? "")
                  : t.menuShowTranslated}
              </Chip>
            ) : null}
          </div>

          {error ? (
            <p
              className="text-[12px] px-3 py-2 mt-3 rounded-md"
              style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
            >
              {error}
            </p>
          ) : null}

          {/* dishes */}
          <div style={{ marginTop: 18 }}>
            {ranked.map((item, idx) => {
              const displayName = effectiveShowTranslated ? item.nameTranslated : item.name;
              const displayDesc = effectiveShowTranslated
                ? item.descriptionTranslated
                : item.description;
              const showOriginalAlso = effectiveShowTranslated && item.name !== item.nameTranslated;
              const conf = confLevelFromScore(item.confidence);
              const isTop = mode === "top3";
              const isExpanded = expandedIdx === idx;
              return (
                <article
                  key={`${item.name}-${idx}`}
                  style={{
                    padding: "18px 0",
                    borderTop: "1px solid var(--nav-border)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                    className="text-left w-full"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                    aria-expanded={isExpanded}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          {isTop ? (
                            <span
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: 9,
                                padding: "2px 6px",
                                borderRadius: 3,
                                background: "var(--accent)",
                                color: "var(--accent-ink)",
                                letterSpacing: "0.08em",
                                fontWeight: 700,
                              }}
                            >
                              {t.menuTopBadge(idx + 1)}
                            </span>
                          ) : null}
                          <Conf level={conf} />
                        </div>
                        <h3
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "var(--ink)",
                            lineHeight: 1.25,
                          }}
                        >
                          {displayName}
                        </h3>
                        {showOriginalAlso ? (
                          <p
                            style={{
                              fontFamily: "var(--font-display)",
                              fontStyle: "italic",
                              fontSize: 12,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            {item.name}
                          </p>
                        ) : null}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          className="tabular"
                          style={{
                            fontFamily: "var(--font-display)",
                            fontSize: 24,
                            fontWeight: 800,
                            color: "var(--ink)",
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {item.estimatedKcal}
                        </div>
                        {item.priceText ? (
                          <MonoMeta>{item.priceText.toUpperCase()}</MonoMeta>
                        ) : null}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 10,
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <MonoMeta>
                        {item.estimatedProteinG !== null
                          ? `B ${Math.round(item.estimatedProteinG)}`
                          : ""}
                        {item.estimatedFatG !== null
                          ? ` · T ${Math.round(item.estimatedFatG)}`
                          : ""}
                        {item.estimatedCarbsG !== null
                          ? ` · W ${Math.round(item.estimatedCarbsG)}`
                          : ""}
                      </MonoMeta>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--accent)",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {isExpanded ? t.menuCollapse : t.menuExpand}
                      </span>
                    </div>
                  </button>

                  {isExpanded ? (
                    <div style={{ marginTop: 12 }}>
                      {displayDesc ? (
                        <p
                          style={{
                            fontSize: 13,
                            color: "var(--ink-2)",
                            lineHeight: 1.5,
                            marginBottom: 12,
                          }}
                        >
                          {displayDesc}
                        </p>
                      ) : null}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => quickLog(item, idx)}
                          disabled={loggingIdx === idx}
                          className="uppercase disabled:opacity-50"
                          style={{
                            flex: 1,
                            padding: "12px 16px",
                            borderRadius: 10,
                            background: "var(--ink)",
                            color: "var(--bg)",
                            border: "none",
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {loggingIdx === idx ? t.saving : t.menuLogIt}
                        </button>
                        <button
                          type="button"
                          onClick={() => editBeforeLog(item)}
                          className="uppercase"
                          style={{
                            padding: "12px 16px",
                            borderRadius: 10,
                            background: "transparent",
                            color: "var(--ink)",
                            boxShadow: "inset 0 0 0 1px var(--nav-border)",
                            border: "none",
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {t.menuEdit}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>

          <AiFooter />
        </>
      )}
    </ScreenShell>
  );
}
